/**
 * Drill-in for one project (JARVIS-126): stays inside the portfolio page —
 * the breadcrumb bar above still says Portfolio › Project — and renders the
 * project's steps in the shared ArtworkWall with "Step N of M" badges. The
 * header carries the tag editor; every other edit (steps, details, comments)
 * goes through the existing PortfolioManagementModal via "Edit project".
 */
import { EditOutlined } from "@ant-design/icons"
import { Button, Tag, Typography } from "antd"
import { useCallback, useMemo } from "react"
import styled from "styled-components"

import ArtworkTagSelect from "#blocks/ArtworkTagSelect"
import ArtworkWall from "#components/ArtworkWall/ArtworkWall"
import {
  categoryColorMap,
  categoryLabelMap,
} from "#components/PortfolioManagement/portfolioPhotoGridConstants"
import { type PortfolioMockProject } from "#components/PortfolioManagement/portfolioUiTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { COLORS } from "#styles/colors"
import { type ArtworkTagOption } from "#utils/artworkTagHelpers"

import { buildProjectWallItems } from "./portfolioPageHelpers"

const { Text } = Typography

/** Separate from the other walls' keys so the drill-in remembers its own zoom. */
export const PORTFOLIO_DRILL_IN_PREFS_KEY = "jarvis-portfolio-drill-in-prefs"

const SRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`

const SHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 8px 20px 10px;
  border-bottom: 1px solid ${COLORS.GRAY3};
  flex: 0 0 auto;
`

const SWallHost = styled.div`
  flex: 1 1 auto;
  min-height: 0;
`

export interface PortfolioDrillInProps {
  project: PortfolioMockProject
  student: Pick<StudentConstructor, "id" | "name" | "artwork"> | null
  /** The whole active dictionary, most-used first. */
  tagOptions: readonly ArtworkTagOption[]
  onTagIdsChange: (projectId: string, tagIds: Uuid[]) => void
  /** Present here (and only here) so the editor can grow the vocabulary. */
  onCreateTag: (text: string) => Promise<Uuid | null>
  onEdit: (projectId: string) => void
}

const PortfolioDrillIn = ({
  project,
  student,
  tagOptions,
  onTagIdsChange,
  onCreateTag,
  onEdit,
}: PortfolioDrillInProps) => {
  const items = useMemo(
    () => buildProjectWallItems(project, student),
    [project, student]
  )

  const handleTagIds = useCallback(
    (tagIds: Uuid[]) => onTagIdsChange(project.id, tagIds),
    [onTagIdsChange, project.id]
  )

  const withImages = items.length
  const total = project.stages.length

  return (
    <SRoot data-testid="portfolio-drill-in" data-project-id={project.id}>
      <SHeader>
        <Text strong style={{ fontSize: 16 }} data-testid="drill-in-title">
          {project.title}
        </Text>
        <Tag color={categoryColorMap[project.category]} style={{ margin: 0 }}>
          {categoryLabelMap[project.category]}
        </Tag>
        <Text type="secondary" data-testid="drill-in-count">
          {total} {total === 1 ? "step" : "steps"}
          {withImages !== total ? ` · ${withImages} with images` : ""}
        </Text>
        <ArtworkTagSelect
          testId="drill-in-tags"
          ariaLabel="Project tags"
          placeholder="Tags — medium, theme, series…"
          value={project.tagIds}
          onChange={handleTagIds}
          options={tagOptions}
          onCreateTag={onCreateTag}
          style={{ flex: "1 1 240px", maxWidth: 520 }}
        />
        <Button
          icon={<EditOutlined />}
          onClick={() => onEdit(project.id)}
          data-testid="drill-in-edit"
        >
          Edit project
        </Button>
      </SHeader>
      <SWallHost>
        <ArtworkWall
          items={items}
          dedupeVersions={false}
          prefsStorageKey={PORTFOLIO_DRILL_IN_PREFS_KEY}
          emptyText={
            total === 0
              ? "No steps yet"
              : "None of this project's steps has an image yet"
          }
        />
      </SWallHost>
    </SRoot>
  )
}

export default PortfolioDrillIn
